import {
  Menu,
  X,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Code2,
  FileText,
  Zap,
  BarChart3,
  Settings,
  Clock,
  Database,
  Building2,
  ChevronsUpDown,
  LogOut,
  Mail,
  MessageCircle,
  KeyRound,
  Loader2,
  MoreHorizontal,
  Pencil,
  Shield,
  Trash2,
  Users,
  UserCircle2,
  Sparkles,
  BrainCircuit,
  ServerCog,
  Plug,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import ThemeSwitcher from "./ThemeSwitcher";
import LanguageSwitcher from "./LanguageSwitcher";
import DesktopConsoleBindingCard from "./DesktopConsoleBindingCard";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useContextStore } from "@store/context";
import { cn } from "@/utils/cn";
import BrandLogo from "./BrandLogo";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/providers/NotificationProvider";
import {
  getHankoElementsLanguage,
  registerHankoElements,
} from "@/services/hankoAuth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useOperateStore } from "@/store/operate";
import { spacesAPI, type SpaceListedSession } from "@/services/api/spaces";
import { routePosture } from "@/utils/posture";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import AgentSpecSidebarNavigation from "@/components/studio/AgentSpecSidebarNavigation";
import AgentSpecStudioThreadsSidebar from "@/components/studio/AgentSpecStudioThreadsSidebar";

interface SidebarProps {
  open: boolean;
  width: number;
  mobile?: boolean;
  onToggle: () => void;
}

export default function Sidebar({
  open,
  width,
  mobile = false,
  onToggle,
}: SidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const posture = routePosture(location.pathname);
  const org = useContextStore((state) => state.org);
  const deploymentId = useContextStore((state) => state.deploymentId);
  const workspace = useContextStore((state) => state.workspace);
  const spaces = useContextStore((state) => state.spaces);
  const organizationsInContext = useContextStore(
    (state) => state.organizations,
  );
  const setWorkspace = useContextStore((state) => state.setWorkspace);
  const upsertSpaceSession = useContextStore(
    (state) => state.upsertSpaceSession,
  );
  const removeSpaceSession = useContextStore(
    (state) => state.removeSpaceSession,
  );
  const { canRead, canWrite, canDeploy, canManageUsers, role } =
    usePermissions();
  const { authMode, logout, user, userProfile, organizations } = useAuth();
  const currentSessionId = useOperateStore((state) => state.currentSessionId);
  const setCurrentSessionId = useOperateStore(
    (state) => state.setCurrentSessionId,
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [expandedSpaceId, setExpandedSpaceId] = useState<string | null>(
    workspace,
  );
  const [visibleSessionCounts, setVisibleSessionCounts] = useState<
    Record<string, number>
  >({});
  const [sessionMenuOpenId, setSessionMenuOpenId] = useState<string | null>(
    null,
  );
  const [sessionActionTarget, setSessionActionTarget] = useState<{
    spaceId: string;
    session: SpaceListedSession;
  } | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [sessionActionLoading, setSessionActionLoading] = useState(false);

  type NavItem = {
    icon: typeof Sparkles;
    label: string;
    href: string;
    accentColor: string;
    group?: string;
  };

  const navItems: NavItem[] =
    posture === "build"
      ? (
          [
            {
              icon: ArrowLeftRight,
              label: t("sidebar.importExport"),
              href: "/build/import-export",
              accentColor: "orange",
              group: "core",
            },
            {
              icon: BrainCircuit,
              label: t("sidebar.llm"),
              href: "/build/llm",
              accentColor: "violet",
              group: "core",
            },
            {
              icon: ServerCog,
              label: t("sidebar.mcp"),
              href: "/build/mcp",
              accentColor: "cyan",
              group: "core",
            },
            {
              icon: Plug,
              label: t("sidebar.integrations", {
                defaultValue: "Intégrations",
              }),
              href: "/build/integrations",
              accentColor: "cyan",
              group: "core",
            },
            {
              icon: Sparkles,
              label: t("sidebar.skills"),
              href: "/build/skills",
              accentColor: "violet",
              group: "core",
            },
            {
              icon: Settings,
              label: t("sidebar.internalRuntime", {
                defaultValue: "Runtime interne",
              }),
              href: "/build/internal",
              accentColor: "sky",
              group: "core",
            },
            {
              icon: MessageCircle,
              label: t("sidebar.messaging"),
              href: "/build/messaging",
              accentColor: "cyan",
              group: "core",
            },
            {
              icon: KeyRound,
              label: t("sidebar.access"),
              href: "/build/access",
              accentColor: "violet",
              group: "core",
            },
            {
              icon: Users,
              label: t("sidebar.spaces"),
              href: "/build/spaces",
              accentColor: "sky",
              group: "core",
            },
            {
              icon: Code2,
              label: t("sidebar.variables", { defaultValue: "Variables" }),
              href: "/build/variables",
              accentColor: "amber",
              group: "core",
            },
            {
              icon: Database,
              label: t("sidebar.data"),
              href: "/build/data",
              accentColor: "emerald",
              group: "advanced",
            },
            {
              icon: KeyRound,
              label: t("sidebar.apiKeys", { defaultValue: "Clés d'API" }),
              href: "/build/api-keys",
              accentColor: "emerald",
              group: "advanced",
            },
          ] as NavItem[]
        ).filter((item) => {
          if (item.href === "/build/api-keys") return canDeploy();
          if (
            item.href === "/build/import-export" ||
            item.href === "/build/llm" ||
            item.href === "/build/mcp" ||
            item.href === "/build/integrations" ||
            item.href === "/build/skills" ||
            item.href === "/build/internal" ||
            item.href === "/build/messaging" ||
            item.href === "/build/access" ||
            item.href === "/build/spaces" ||
            item.href === "/build/variables"
          )
            return canDeploy();
          return canWrite("recipes");
        })
      : posture === "operate"
        ? (
            [
              {
                icon: Zap,
                label: t("sidebar.chat"),
                href: "/operate/chat",
                accentColor: undefined as string | undefined,
              },
              {
                icon: BarChart3,
                label: t("sidebar.catalog"),
                href: "/operate/catalog",
                accentColor: undefined as string | undefined,
              },
              {
                icon: Clock,
                label: t("sidebar.history"),
                href: "/operate/history",
                accentColor: undefined as string | undefined,
              },
            ] as NavItem[]
          ).filter((item) => {
            if (item.href === "/operate/history") return canRead("executions");
            if (item.href === "/operate/chat") return canRead("executions");
            return canRead("recipes");
          })
        : (
            [
              {
                icon: MessageCircle,
                label: t("agentSpec.calistoNavigation"),
                href: "/studio/chat",
                accentColor: "violet",
                group: "core",
              },
              {
                icon: FileText,
                label: t("agentSpec.title"),
                href: "/studio/agent-spec",
                accentColor: "violet",
                group: "core",
              },
              {
                icon: KeyRound,
                label: t("sidebar.agentControl", {
                  defaultValue: "Contrôle agent",
                }),
                href: "/studio/agent-control",
                accentColor: "emerald",
                group: "advanced",
              },
            ] as NavItem[]
          ).filter((item) => {
            if (item.href === "/studio/agent-control") return canManageUsers();
            return canWrite("recipes");
          });

  const isActive = (href: string) => {
    return location.pathname.startsWith(href);
  };

  const getAccentColorClasses = (accentColor?: string) => {
    const colorMap: Record<string, { active: string; inactive: string }> = {
      violet: {
        active:
          "text-violet-700 dark:text-violet-400 bg-violet-100 dark:bg-violet-500/10 border-violet-600 dark:border-violet-400",
        inactive: "text-violet-600 dark:text-violet-400/60",
      },
      cyan: {
        active:
          "text-cyan-700 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-500/10 border-cyan-600 dark:border-cyan-400",
        inactive: "text-cyan-600 dark:text-cyan-400/60",
      },
      amber: {
        active:
          "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/10 border-amber-600 dark:border-amber-400",
        inactive: "text-amber-600 dark:text-amber-400/60",
      },
      emerald: {
        active:
          "text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10 border-emerald-600 dark:border-emerald-400",
        inactive: "text-emerald-600 dark:text-emerald-400/60",
      },
      sky: {
        active:
          "text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-500/10 border-sky-600 dark:border-sky-400",
        inactive: "text-sky-600 dark:text-sky-400/60",
      },
      rose: {
        active:
          "text-rose-700 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/10 border-rose-600 dark:border-rose-400",
        inactive: "text-rose-600 dark:text-rose-400/60",
      },
    };
    return colorMap[accentColor || "violet"] || colorMap.violet;
  };

  const currentWorkspace = useMemo(
    () => spaces.find((item) => item.id === workspace) || null,
    [spaces, workspace],
  );
  const currentOrganization = useMemo(
    () => organizationsInContext.find((item) => item.id === org) || null,
    [org, organizationsInContext],
  );
  const spacesForDeployment = useMemo(() => {
    if (!deploymentId) {
      return spaces;
    }

    return spaces.filter((item) => {
      const scopedDeploymentIds = [
        ...(Array.isArray(item.deploymentIds) ? item.deploymentIds : []),
        ...(item.deploymentId ? [item.deploymentId] : []),
      ];

      if (scopedDeploymentIds.length === 0) {
        return true;
      }

      return scopedDeploymentIds.includes(deploymentId);
    });
  }, [deploymentId, spaces]);
  const activeOperateSpaceId = useMemo(() => {
    if (!workspace) {
      return null;
    }
    return spacesForDeployment.some((item) => item.id === workspace)
      ? workspace
      : null;
  }, [spacesForDeployment, workspace]);
  const sortedSpacesForDeployment = useMemo(() => {
    return spacesForDeployment.slice().sort((left, right) => {
      const byName = left.name.localeCompare(right.name, i18n.language, {
        sensitivity: "base",
      });
      if (byName !== 0) {
        return byName;
      }
      return left.id.localeCompare(right.id);
    });
  }, [i18n.language, spacesForDeployment]);

  useEffect(() => {
    if (!expandedSpaceId && activeOperateSpaceId) {
      setExpandedSpaceId(activeOperateSpaceId);
      return;
    }

    if (
      expandedSpaceId &&
      !sortedSpacesForDeployment.some((item) => item.id === expandedSpaceId)
    ) {
      setExpandedSpaceId(
        activeOperateSpaceId || sortedSpacesForDeployment[0]?.id || null,
      );
    }
  }, [activeOperateSpaceId, expandedSpaceId, sortedSpacesForDeployment]);

  const displayName =
    userProfile?.displayName || user?.displayName || user?.email || "User";
  const userEmail = userProfile?.email || user?.email || "No email";
  const authLabel =
    authMode === "local"
      ? t("sidebar.localAuthentication")
      : t("sidebar.hankoAuthentication");
  const authSignOutHint =
    authMode === "local"
      ? t("sidebar.localSignOutHint")
      : t("sidebar.hankoSignOutHint");
  const profileDescription =
    authMode === "hanko"
      ? t("sidebar.hankoProfileDescription")
      : authSignOutHint;
  const hankoLanguage = getHankoElementsLanguage(
    i18n.resolvedLanguage || i18n.language,
  );
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  useEffect(() => {
    if (!profileOpen || authMode !== "hanko") {
      return;
    }
    registerHankoElements().catch((error) => {
      console.error("Failed to load account security element:", error);
      toast.error(t("sidebar.hankoProfileLoadError"));
    });
  }, [authMode, profileOpen, t]);

  const handleLogout = async () => {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);
    try {
      await logout();
      setLogoutConfirmOpen(false);
      setProfileOpen(false);
      navigate("/", { replace: true });
      toast.success(authSignOutHint);
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error(
        error instanceof Error ? error.message : t("sidebar.signOut"),
      );
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSpaceSelect = (spaceId: string) => {
    setWorkspace(spaceId);
    setCurrentSessionId(null);
    setExpandedSpaceId(spaceId);
    navigate("/operate/chat");
    if (mobile) {
      onToggle();
    }
  };

  const handleSessionSelect = (spaceId: string, sessionId: string) => {
    if (workspace !== spaceId) {
      setWorkspace(spaceId);
    }
    setExpandedSpaceId(spaceId);
    setCurrentSessionId(sessionId);
    // Preserve organizationId/deploymentId — dropping them forces a second
    // ContextProvider replace and races with ChatPage session URL sync.
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("session", sessionId);
    nextParams.delete("newSession");
    nextParams.delete("workflow");
    nextParams.delete("autorunWorkflow");
    navigate(`/operate/chat?${nextParams.toString()}`, { replace: true });
    if (mobile) {
      onToggle();
    }
  };

  const openRenameSessionDialog = (
    spaceId: string,
    session: SpaceListedSession,
  ) => {
    setSessionMenuOpenId(null);
    setSessionActionTarget({ spaceId, session });
    setRenameValue(session.name || "");
    setRenameDialogOpen(true);
  };

  const openDeleteSessionDialog = (
    spaceId: string,
    session: SpaceListedSession,
  ) => {
    setSessionMenuOpenId(null);
    setSessionActionTarget({ spaceId, session });
    setDeleteDialogOpen(true);
  };

  const handleRenameSession = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!sessionActionTarget || sessionActionLoading) {
      return;
    }

    const nextName = renameValue.trim();
    if (!nextName) {
      toast.error(t("sidebar.renameSessionNameRequired"));
      return;
    }

    setSessionActionLoading(true);
    try {
      const updatedSession = await spacesAPI.updateSession(
        sessionActionTarget.session.id,
        { name: nextName },
      );
      upsertSpaceSession(sessionActionTarget.spaceId, {
        ...sessionActionTarget.session,
        ...updatedSession,
        name: updatedSession.name || nextName,
        updatedAt: updatedSession.updatedAt || new Date().toISOString(),
        lastActivityAt:
          updatedSession.lastActivityAt ||
          sessionActionTarget.session.lastActivityAt,
      });
      setRenameDialogOpen(false);
      setSessionActionTarget(null);
      toast.success(t("sidebar.renameSessionSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("sidebar.renameSessionError"),
      );
    } finally {
      setSessionActionLoading(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionActionTarget || sessionActionLoading) {
      return;
    }

    const { spaceId, session } = sessionActionTarget;
    setSessionActionLoading(true);
    try {
      await spacesAPI.closeSession(spaceId, session.id);
      removeSpaceSession(spaceId, session.id);
      if (currentSessionId === session.id) {
        setCurrentSessionId(null);
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete("session");
        nextParams.delete("workflow");
        nextParams.delete("autorunWorkflow");
        nextParams.delete("newSession");
        nextParams.set("newSession", String(Date.now()));
        navigate(`/operate/chat?${nextParams.toString()}`, { replace: true });
      }
      setDeleteDialogOpen(false);
      setSessionActionTarget(null);
      toast.success(t("sidebar.deleteSessionSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("sidebar.deleteSessionError"),
      );
    } finally {
      setSessionActionLoading(false);
    }
  };

  const expanded = mobile ? true : open;

  return (
    <div
      className={cn(
        "bg-sidebar border-r border-sidebar-border transition-all duration-200 ease-in-out flex flex-col",
        mobile
          ? "fixed inset-y-0 left-0 z-40 w-[min(82vw,320px)] max-w-[320px] shadow-2xl"
          : open
            ? "w-64"
            : "w-16",
        mobile &&
          (open ? "translate-x-0" : "-translate-x-full pointer-events-none"),
      )}
      style={!mobile && open ? { width: `${width}px` } : undefined}
    >
      {/* Header */}
      <div
        className={cn(
          "relative border-b border-sidebar-border flex items-center",
          expanded ? "h-16 px-4" : "h-14 px-3",
        )}
      >
        <div className={cn("min-w-0", expanded ? "flex-1" : "mx-auto")}>
          <BrandLogo
            collapsed={!expanded}
            className={expanded ? "max-w-[11rem]" : ""}
          />
        </div>
        <button
          onClick={onToggle}
          className={cn(
            "p-1 hover:bg-sidebar-accent rounded transition-colors",
            expanded ? "" : "absolute right-3",
          )}
        >
          {expanded ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {expanded && navItems.length > 0 && (
          <div className="my-2 border-t border-border/40" />
        )}

        {navItems.map((item, index) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          const accentClasses = getAccentColorClasses(item.accentColor);
          const prevGroup = index > 0 ? navItems[index - 1]?.group : null;
          const showSeparator =
            item.group && prevGroup && item.group !== prevGroup;
          const isAdvanced = item.group === "advanced";

          return (
            <div key={item.href}>
              {showSeparator && item.group === "advanced" && expanded && (
                <>
                  <div className="my-2 border-t border-border/40" />
                  <div className="px-3 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                    Avancé
                  </div>
                </>
              )}
              {showSeparator && item.group !== "advanced" && (
                <div className="my-2 border-t border-border/40" />
              )}
              <Link
                to={item.href}
                onClick={() => {
                  if (mobile) {
                    onToggle();
                  }
                }}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  expanded ? "" : "justify-center",
                  isAdvanced && expanded && "opacity-75",
                  active
                    ? `${accentClasses.active} font-medium border-l-2`
                    : `${accentClasses.inactive} hover:bg-sidebar-accent hover:text-foreground`,
                )}
                title={!expanded ? item.label : undefined}
              >
                <Icon size={20} className="flex-shrink-0" />
                {expanded && <span>{item.label}</span>}
              </Link>
              {item.href === "/studio/chat" && active ? (
                <AgentSpecStudioThreadsSidebar
                  expanded={expanded}
                  onNavigate={mobile ? onToggle : undefined}
                />
              ) : null}
              {item.href === "/studio/agent-spec" && active ? (
                <AgentSpecSidebarNavigation
                  expanded={expanded}
                  onNavigate={mobile ? onToggle : undefined}
                />
              ) : null}
            </div>
          );
        })}

        {posture === "operate" && expanded && (
          <div className="mt-4 space-y-2 border-t border-sidebar-border pt-3">
            <div className="px-3 text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/50">
              {t("sidebar.spacesSessions")}
            </div>

            {spacesForDeployment.length === 0 ? (
              <div className="px-3 text-xs text-sidebar-foreground/50">
                {t("sidebar.noSpaceForDeployment")}
              </div>
            ) : (
              sortedSpacesForDeployment.map((item) => {
                const isCurrentSpace = item.id === activeOperateSpaceId;
                const isExpanded = expandedSpaceId === item.id;
                const sortedSessions = (item.sessions || [])
                  .slice()
                  .sort((left, right) => {
                    const rightTime = new Date(
                      right.lastActivityAt || right.updatedAt,
                    ).getTime();
                    const leftTime = new Date(
                      left.lastActivityAt || left.updatedAt,
                    ).getTime();
                    return rightTime - leftTime;
                  });
                const visibleCount = visibleSessionCounts[item.id] ?? 4;
                const visibleSessions = sortedSessions.slice(0, visibleCount);
                const hiddenSessions = Math.max(
                  sortedSessions.length - visibleSessions.length,
                  0,
                );
                const latestSession = sortedSessions[0] || null;
                return (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-sidebar-border/70 bg-sidebar-accent/25 p-2"
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedSpaceId(isExpanded ? null : item.id)
                        }
                        className="mt-1 rounded-md p-1 text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-foreground"
                        aria-label={
                          isExpanded ? t("common.hide") : t("common.show")
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSpaceSelect(item.id)}
                        className={cn(
                          "flex min-w-0 flex-1 flex-col rounded-xl px-3 py-2 text-left transition-colors",
                          isCurrentSpace
                            ? "bg-sidebar-accent text-foreground"
                            : "text-muted-foreground hover:bg-sidebar-accent hover:text-foreground",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">
                            {item.name}
                          </span>
                          {isCurrentSpace && (
                            <span className="rounded bg-foreground/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                              {t("common.active")}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-sidebar-foreground/55">
                          <span>
                            {t("sidebar.sessionCount", {
                              count: sortedSessions.length,
                            })}
                          </span>
                          {latestSession && (
                            <span>
                              {t("sidebar.lastActivity", {
                                value: new Date(
                                  latestSession.lastActivityAt ||
                                    latestSession.updatedAt,
                                ).toLocaleDateString(i18n.language),
                              })}
                            </span>
                          )}
                        </div>
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="space-y-1 pl-8 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedSpaceId(item.id);
                            setVisibleSessionCounts((current) => ({
                              ...current,
                              [item.id]: 4,
                            }));
                            if (workspace !== item.id) {
                              setWorkspace(item.id);
                            }
                            setCurrentSessionId(null);
                            const nextParams = new URLSearchParams(
                              location.search,
                            );
                            nextParams.delete("session");
                            nextParams.delete("workflow");
                            nextParams.delete("autorunWorkflow");
                            nextParams.set("newSession", String(Date.now()));
                            navigate(`/operate/chat?${nextParams.toString()}`);
                          }}
                          className={cn(
                            "flex w-full items-center rounded-md px-3 py-1.5 text-left text-xs transition-colors",
                            !currentSessionId
                              ? "bg-sidebar-accent/70 text-foreground"
                              : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-foreground",
                          )}
                        >
                          {t("sidebar.newSession")}
                        </button>

                        {(item.sessions || []).length === 0 && (
                          <div className="px-3 py-1 text-xs text-sidebar-foreground/50">
                            {t("sidebar.noSessionYet")}
                          </div>
                        )}

                        {visibleSessions.map((session) => {
                          const isCurrentSession =
                            session.id === currentSessionId;
                          const lastActivity = new Date(
                            session.lastActivityAt ||
                              session.updatedAt ||
                              session.createdAt,
                          );
                          const now = new Date();
                          const minutesAgo = Math.floor(
                            (now.getTime() - lastActivity.getTime()) /
                              (1000 * 60),
                          );
                          const hoursAgo = Math.floor(minutesAgo / 60);

                          let dotColor = "bg-gray-400";
                          let isAnimated = false;
                          if (minutesAgo < 60) {
                            dotColor = "bg-green-500";
                            isAnimated = true;
                          } else if (hoursAgo < 24) {
                            dotColor = "bg-green-500";
                            isAnimated = false;
                          } else {
                            dotColor = "bg-gray-400";
                            isAnimated = false;
                          }

                          const sessionLabel =
                            session.name?.trim() && session.name.trim() !== "Session"
                              ? session.name.trim()
                              : t("sidebar.unnamedSession");
                          const tooltipText = `${sessionLabel} • ${new Date(session.createdAt).toLocaleDateString(i18n.language)}`;

                          return (
                            <div
                              key={session.id}
                              className="flex items-center gap-1"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  handleSessionSelect(item.id, session.id)
                                }
                                onContextMenu={(event) => {
                                  event.preventDefault();
                                  setSessionMenuOpenId(session.id);
                                }}
                                title={tooltipText}
                                className={cn(
                                  "flex min-w-0 flex-1 items-center justify-between rounded-md px-3 py-1.5 text-left text-xs transition-colors border-l-2",
                                  isCurrentSession
                                    ? "bg-sidebar-accent/70 text-foreground border-foreground/40"
                                    : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-foreground border-transparent",
                                )}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span
                                    className={cn(
                                      "h-1.5 w-1.5 rounded-full flex-shrink-0",
                                      dotColor,
                                      isAnimated && "animate-pulse",
                                    )}
                                  />
                                  <span className="truncate">{sessionLabel}</span>
                                </div>
                                <span className="ml-2 shrink-0 text-[10px] text-sidebar-foreground/40">
                                  {lastActivity.toLocaleDateString(
                                    i18n.language,
                                  )}
                                </span>
                              </button>

                              <DropdownMenu.Root
                                open={sessionMenuOpenId === session.id}
                                onOpenChange={(open) =>
                                  setSessionMenuOpenId(open ? session.id : null)
                                }
                              >
                                <DropdownMenu.Trigger asChild>
                                  <button
                                    type="button"
                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/50 transition-colors hover:bg-sidebar-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label={t("sidebar.sessionActions")}
                                    title={t("sidebar.sessionActions")}
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Portal>
                                  <DropdownMenu.Content
                                    align="end"
                                    sideOffset={4}
                                    className="z-50 min-w-40 rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-lg"
                                  >
                                    <DropdownMenu.Item
                                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                      onSelect={() =>
                                        openRenameSessionDialog(
                                          item.id,
                                          session,
                                        )
                                      }
                                    >
                                      <Pencil className="h-4 w-4" />
                                      {t("sidebar.renameSession")}
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-destructive outline-none hover:bg-destructive/10 focus:bg-destructive/10"
                                      onSelect={() =>
                                        openDeleteSessionDialog(
                                          item.id,
                                          session,
                                        )
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      {t("sidebar.deleteSession")}
                                    </DropdownMenu.Item>
                                  </DropdownMenu.Content>
                                </DropdownMenu.Portal>
                              </DropdownMenu.Root>
                            </div>
                          );
                        })}
                        {hiddenSessions > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setVisibleSessionCounts((current) => ({
                                ...current,
                                [item.id]: sortedSessions.length,
                              }))
                            }
                            className="w-full rounded-md px-3 py-1.5 text-left text-[11px] text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-foreground"
                          >
                            {t("sidebar.showMoreSessions", {
                              count: hiddenSessions,
                            })}
                          </button>
                        )}
                        {sortedSessions.length > 4 && hiddenSessions === 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setVisibleSessionCounts((current) => ({
                                ...current,
                                [item.id]: 4,
                              }))
                            }
                            className="w-full rounded-md px-3 py-1.5 text-left text-[11px] text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-foreground"
                          >
                            {t("sidebar.showFewerSessions")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div
        className={cn(
          "border-t border-sidebar-border p-2 space-y-2",
          expanded ? "" : "flex flex-col items-center",
        )}
      >
        {expanded && (
          <div className="flex items-center gap-2 justify-center px-1 py-2">
            <ThemeSwitcher compact />
            <LanguageSwitcher compact />
          </div>
        )}

        {user && (
          <>
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-3 py-2 text-left transition-colors hover:bg-sidebar-accent",
                expanded ? "" : "w-12 justify-center px-0",
              )}
              title={
                !expanded
                  ? `${displayName} · ${t("sidebar.userProfile")}`
                  : undefined
              }
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-foreground/10 text-xs font-semibold text-foreground">
                {initials || "U"}
              </div>
              {expanded && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-sidebar-foreground">
                      {displayName}
                    </div>
                    <div className="truncate text-xs text-sidebar-foreground/60">
                      {authMode === "local"
                        ? t("sidebar.localProfile")
                        : userEmail}
                    </div>
                  </div>
                  <ChevronsUpDown
                    size={16}
                    className="text-sidebar-foreground/60"
                  />
                </>
              )}
            </button>

            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogContent
                className={cn(
                  "flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden p-0",
                  authMode === "hanko"
                    ? "w-[min(96vw,72rem)] max-w-5xl"
                    : "max-w-md",
                )}
              >
                <DialogHeader className="shrink-0 border-b border-border px-6 py-5">
                  <DialogTitle>{t("sidebar.userProfile")}</DialogTitle>
                  <DialogDescription>{profileDescription}</DialogDescription>
                </DialogHeader>

                <div
                  className={cn(
                    "min-h-0 flex-1 overflow-y-auto",
                    authMode === "hanko"
                      ? "grid gap-0 lg:grid-cols-[19rem_minmax(0,1fr)]"
                      : "space-y-4 px-6 py-5",
                  )}
                >
                  <div
                    className={cn(
                      "space-y-4",
                      authMode === "hanko"
                        ? "border-b border-border bg-muted/20 p-5 lg:border-b-0 lg:border-r"
                        : "",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-4",
                        authMode === "hanko"
                          ? "rounded-xl border border-border bg-background p-4"
                          : "rounded-2xl border border-border bg-muted/30 p-4",
                      )}
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-foreground/10 text-base font-semibold text-foreground">
                        {initials || "U"}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-base font-semibold">
                          {displayName}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">
                          {userEmail}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <div className="rounded-xl border border-border bg-background/70 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <Mail className="h-4 w-4" />
                          {t("sidebar.account")}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">
                          {userEmail}
                        </div>
                      </div>

                      <div className="rounded-xl border border-border bg-background/70 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <Shield className="h-4 w-4" />
                          {t("sidebar.access")}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {role} · {authLabel}
                        </div>
                      </div>

                      <div className="rounded-xl border border-border bg-background/70 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <Building2 className="h-4 w-4" />
                          {t("sidebar.organizations")}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t("sidebar.memberships", {
                            count: organizations.length,
                          })}
                          {currentOrganization
                            ? ` · ${currentOrganization.name}`
                            : ""}
                        </div>
                      </div>

                      <div className="rounded-xl border border-border bg-background/70 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <UserCircle2 className="h-4 w-4" />
                          {t("sidebar.currentWorkspace")}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {currentWorkspace?.name ||
                            t("sidebar.noWorkspaceSelected")}
                        </div>
                      </div>

                      <DesktopConsoleBindingCard />
                    </div>
                  </div>

                  {authMode === "hanko" && (
                    <section className="min-w-0 space-y-4 bg-background p-5">
                      <div className="flex items-center gap-2 border-b border-border pb-3 text-sm font-semibold">
                        <KeyRound className="h-4 w-4" />
                        {t("sidebar.hankoProfile")}
                      </div>
                      <hanko-profile
                        className="agent-hanko-profile"
                        lang={hankoLanguage}
                      />
                    </section>
                  )}
                </div>

                <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4 sm:justify-between">
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>{authSignOutHint}</span>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setLogoutConfirmOpen(true)}
                    disabled={loggingOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {authMode === "local"
                      ? t("sidebar.switchUser")
                      : t("sidebar.signOut")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              open={logoutConfirmOpen}
              onOpenChange={setLogoutConfirmOpen}
            >
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("sidebar.confirmSignOutTitle")}</DialogTitle>
                  <DialogDescription>
                    {authMode === "local"
                      ? t("sidebar.confirmSwitchUserDescription")
                      : t("sidebar.confirmSignOutDescription")}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLogoutConfirmOpen(false)}
                    disabled={loggingOut}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => void handleLogout()}
                    disabled={loggingOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {authMode === "local"
                      ? t("sidebar.switchUser")
                      : t("sidebar.signOut")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        <Dialog
          open={renameDialogOpen}
          onOpenChange={(open) => {
            if (sessionActionLoading) return;
            setRenameDialogOpen(open);
            if (!open) {
              setSessionActionTarget(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <form onSubmit={handleRenameSession} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{t("sidebar.renameSessionTitle")}</DialogTitle>
                <DialogDescription>
                  {t("sidebar.renameSessionDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="session-name">
                  {t("sidebar.sessionName")}
                </label>
                <Input
                  id="session-name"
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                  autoFocus
                  disabled={sessionActionLoading}
                />
              </div>
              <DialogFooter className="gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRenameDialogOpen(false)}
                  disabled={sessionActionLoading}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={sessionActionLoading}>
                  {sessionActionLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("sidebar.renameSession")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            if (sessionActionLoading) return;
            setDeleteDialogOpen(open);
            if (!open) {
              setSessionActionTarget(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("sidebar.deleteSessionTitle")}</DialogTitle>
              <DialogDescription>
                {t("sidebar.deleteSessionDescription", {
                  name:
                    sessionActionTarget?.session.name ||
                    sessionActionTarget?.session.id ||
                    "",
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={sessionActionLoading}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void handleDeleteSession()}
                disabled={sessionActionLoading}
              >
                {sessionActionLoading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("sidebar.deleteSession")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div
          className={cn(
            "text-xs text-sidebar-foreground/60",
            expanded ? "px-2" : "text-center",
          )}
        >
          {t("sidebar.access")} · {role}
        </div>
      </div>
    </div>
  );
}
